import { ArrowLeft } from "lucide-react";
import * as Popover from "@radix-ui/react-popover";
import { Button } from "@/components/ui";
import { DatePicker } from "@/components/ui/DatePicker";
import { CategoryPicker } from "../CategoryPicker";
import { FileIcon } from "../FileIcon";
import type { UploadFile } from "@/hooks/use-upload-documents";
import type { Id } from "@/packages/types";

interface CategoryAssignmentViewProps {
  files: UploadFile[];
  onSetFileCategoryId: (
    fileId: string,
    categoryId: Id<"documentCategories">,
  ) => void;
  onSetFileExpirationDate: (fileId: string, date: number | undefined) => void;
  onBack: () => void;
  onUpload: () => void;
  isUploading: boolean;
}

export function CategoryAssignmentView({
  files,
  onSetFileCategoryId,
  onSetFileExpirationDate,
  onBack,
  onUpload,
  isUploading,
}: CategoryAssignmentViewProps) {
  const assignedCount = files.filter((f) => f.categoryId != null).length;
  const allAssigned = assignedCount === files.length;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-primary">
          Assign category to each file
        </h3>
        <span className="text-xs text-muted">
          {assignedCount}/{files.length} assigned
        </span>
      </div>

      <div className="space-y-2 max-h-[400px] overflow-y-auto">
        {files.map((file) => (
          <div
            key={file.id}
            className="flex items-center gap-3 rounded-lg bg-card px-3 py-2.5"
          >
            <FileIcon type={file.fileType} />

            <div className="flex-1 min-w-0">
              <p className="text-sm text-primary truncate">
                {file.displayName}
              </p>
            </div>

            <div className="w-[180px] shrink-0">
              <CategoryPicker
                selectedCategoryId={file.categoryId}
                onSelect={(categoryId) =>
                  onSetFileCategoryId(file.id, categoryId)
                }
                disabled={isUploading}
              />
            </div>

            <div className="w-[140px] shrink-0">
              <DatePicker
                value={
                  file.expirationDate
                    ? new Date(file.expirationDate)
                    : undefined
                }
                onChange={(date) =>
                  onSetFileExpirationDate(file.id, date?.getTime())
                }
                minDate={new Date()}
                autoConfirm
              >
                <DatePicker.Trigger
                  variant="ghost"
                  className="text-[12px] w-full"
                />
                <Popover.Portal>
                  <Popover.Content
                    side="bottom"
                    align="end"
                    sideOffset={4}
                    className="z-[60] rounded-[12px] border border-border bg-card shadow-lg"
                    onOpenAutoFocus={(e) => e.preventDefault()}
                  >
                    <DatePicker.Calendar />
                  </Popover.Content>
                </Popover.Portal>
              </DatePicker>
            </div>
          </div>
        ))}
      </div>

      {!allAssigned && (
        <p className="text-xs text-destructive">
          Please assign a category to all files before uploading
        </p>
      )}

      <div className="flex gap-2 pt-2">
        <Button
          variant="secondary"
          onClick={onBack}
          disabled={isUploading}
          className="flex items-center gap-2"
        >
          <ArrowLeft size={16} />
          Back
        </Button>
        <Button
          variant="primary"
          onClick={onUpload}
          disabled={!allAssigned || isUploading}
          loading={isUploading}
          className="flex-1"
        >
          {isUploading ? "Uploading..." : "Upload"}
        </Button>
      </div>
    </div>
  );
}
